import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Box, IconButton, Tooltip } from '@mui/material';
import { GridOn, Save } from '@mui/icons-material';

import { Selection } from './selection';
import TimeIntervalSelector, { defaultIntervalState, IntervalState } from './TimeIntervalSelector';
import MeasurementStore from '../../stores/meas-store';
import { Filter as MFilter } from '../../http/meas-service';
import Charts from './charts/Charts';
import MapLayout from './maps/MapLayout';
import { DashboardOutletContext } from './DashboardShellLayout';
import { readOnlyFreshCookie, writeOnlyFreshCookie } from './fresh-filter-cookie';

//=================================================================================================
export default function DashBoardLayout() {
    const navigate = useNavigate();
    const { selection, onSelect } = useOutletContext<DashboardOutletContext>();

    const [measStore] = useState(() => new MeasurementStore());
    const [intervalState, setIntervalState] = useState<IntervalState>(defaultIntervalState);
    const [onlyFresh, setOnlyFresh] = useState(readOnlyFreshCookie);

    const buildFilter = (sel: Selection, base: MFilter): MFilter => {
        // prettier-ignore
        switch (sel.kind) {
            case 'node'     : return { ...base, node: sel.nodeId      };
            case 'location' : return { ...base, lo:  [sel.locationId] };
            case 'device'   : return { ...base, dev: [sel.deviceId]   };
            case 'kosa'     : return { ...base, sns: [sel.kosaId]     };
        }
    };

    useEffect(() => {
        const setFilters = async () => {
            try {
                const filter = buildFilter(selection, {
                    fr: intervalState.from.toDate(),
                    to: intervalState.to.toDate(),
                    ov: onlyFresh ? false : undefined,
                });
                await measStore.setFilter(filter);
            } catch (e) {
                //FIXME
                console.log(e);
            }
        };
        setFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection]);

    const handleTimeInterval = useCallback(async (state: IntervalState) => {
        try {
            setIntervalState(state);
            let filter = measStore.filter;
            filter = { ...filter, fr: state.from.toDate(), to: state.to.toDate() };
            await measStore.setFilter(filter);
        } catch (e) {
            //FIXME
            console.log(e);
        }
    }, []);

    const handleOnlyFreshChange = useCallback(async () => {
        try {
            const oF = !onlyFresh;
            setOnlyFresh(oF);
            writeOnlyFreshCookie(oF);
            const filter = { ...measStore.filter, ov: oF ? false : undefined };
            await measStore.setFilter(filter);
        } catch (e) {
            //FIXME
            console.log(e);
        }
    }, [onlyFresh]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <TimeIntervalSelector state={intervalState} onChange={handleTimeInterval} />
                <Tooltip title="Отображать данные с приборов которые пришли не сразу">
                    <IconButton
                        onClick={handleOnlyFreshChange}
                        sx={{ color: !onlyFresh ? 'primary.main' : 'text.disabled' }}
                    >
                        <Save fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Экспорт данных" placement="left">
                    <IconButton
                        onClick={() =>
                            navigate('/export', {
                                state: {
                                    selection,
                                    from: intervalState.from.toISOString(),
                                    to: intervalState.to.toISOString(),
                                },
                            })
                        }
                        sx={{ mr: 1 }}
                    >
                        <GridOn fontSize="small" color="primary" />
                    </IconButton>
                </Tooltip>
            </Box>
            <MapLayout selected={selection} onSelect={onSelect} />
            <Charts store={measStore} selected={selection} onSelect={onSelect} />
        </Box>
    );
}
